import { compose } from 'redux';
import { newHistory } from 'redux-undo';
import { isEmpty, omit } from 'underscore';
import {
  PodcastTemplateConfig,
  PodcastWorkflowTemplate,
} from 'redux/middleware/api/podcast-service';
import { applyConfigTransforms } from 'utils/embed/config-transforms';
import { isSpitfireConfig } from 'utils/embed/embed';
import { getStateContent } from '../core/selectors';
import {
  VideoTemplateEditorConfig,
  VideoTemplateState,
  VideoTemplateStateContent,
} from '../types';
import { getBackgroundColor } from './background';
import { getCaptions } from './captions';
import {
  DEFAULT_BG_COLOR,
  DEFAULT_INTRO_OUTRO_STATE,
  DEFAULT_KEY_TEXT_ASSETS,
  DEFAULT_LAYER_STATE,
  DEFAULT_MAIN_IMAGE,
  DEFAULT_ORDERED_STATE,
  DEFAULT_PATCH,
  DEFAULT_PROGRESS_STATE,
  DEFAULT_SLIDESHOW_STATE,
} from './constants';
import { getIntroOutro } from './edge';
import { getKeyTextAssets } from './key-assets';
import { buildLayerState, identifyLayers } from './layers';
import { getProgress } from './progress';
import { getHasCubeEffect, getSlideshow } from './slideshow';
import { getAspectRatio } from './template';
import { getTextOverlays } from './text';
import { getVideoClips } from './video';
import { getWatermark } from './watermark';
import { getSoundwave, removeDisabledSoundwave } from './waveform';

export function createState(
  content: VideoTemplateStateContent,
): VideoTemplateState {
  return newHistory([], content, []);
}

export function modifyStateContent(
  state: VideoTemplateState,
  modify: (content: VideoTemplateStateContent) => VideoTemplateStateContent,
): VideoTemplateState {
  return {
    ...state,
    present: modify(getStateContent(state)),
  };
}

// TODO: remove this function
// During implementation of hotfix/SPAR-19150, it was discovered that the backend
// is formatting the template config in a way that the front end doesn't expect,
// specifically the config.soundwave.isEnabled property should be called
// config.soundwave.enabled so that it aligns with the shape of the embed configuration.
// template configurations are a subset of the embed configuration, so objects should
// use the same keys.
//
// There is a backend ticket to fix this, however it is too high risk to do as part
// of a hotfix.
function fixTemplateConfig(
  config: PodcastTemplateConfig,
): PodcastTemplateConfig {
  if (!config.soundwave || isEmpty(config.soundwave)) {
    return config;
  }

  // @ts-ignore
  const { isEnabled, enabled, ...restSoundwave } = config.soundwave;

  return {
    ...config,
    soundwave: {
      ...restSoundwave,
      enabled: isEnabled ?? enabled,
    },
  };
}

export function createEmptyState(aspectRatio: number): VideoTemplateState {
  return createState({
    aspectRatio,
    canvas: undefined,
    container: undefined,
    introOutro: DEFAULT_INTRO_OUTRO_STATE,
    layers: DEFAULT_LAYER_STATE,
    mainImage: DEFAULT_MAIN_IMAGE,
    originalLayers: DEFAULT_LAYER_STATE.order,
    patch: DEFAULT_PATCH,
    backgroundColor: DEFAULT_BG_COLOR,
    hasCubeEffect: false,
    slideEffectText: {},
    slideshow: DEFAULT_SLIDESHOW_STATE,
    soundwave: undefined,
    progress: DEFAULT_PROGRESS_STATE,
    textOverlays: DEFAULT_ORDERED_STATE,
    timer: undefined,
    videoClips: DEFAULT_ORDERED_STATE,
    watermark: undefined,
    transcription: {},
    ...DEFAULT_KEY_TEXT_ASSETS,
  });
}

export const createStateFromConfig = compose(
  (config: PodcastTemplateConfig): VideoTemplateState => {
    const layers = identifyLayers(config);
    const { keyAssets: keyTextAssets, ...textState } = getTextOverlays(
      config,
      layers,
    );

    const {
      keyAssets: keySlideshowAssets,
      text: slideEffectText,
      ...slideState
    } = getSlideshow(config, layers);

    const introOutro = getIntroOutro(config);
    const videoState = getVideoClips(config, layers);

    const soundwave = getSoundwave(config, layers);

    const [watermark] = getWatermark(config);

    const layerState = buildLayerState(layers);

    const captions = getCaptions(config);

    const state = {
      aspectRatio: undefined,
      audioInfo: config?.audioInfo,
      backgroundColor: getBackgroundColor(config),
      captions,
      container: undefined,
      canvas: undefined,
      hasCubeEffect: getHasCubeEffect(config),
      introOutro,
      isComplex: isSpitfireConfig(config),
      layers: layerState,
      mainImage: {
        ids: keySlideshowAssets.mainImage,
        src: undefined,
      },
      originalLayers: layerState.order,
      patch: DEFAULT_PATCH,
      progress: getProgress(config) || DEFAULT_PROGRESS_STATE,
      slideshow: slideState,
      slideEffectText,
      soundwave,
      textOverlays: textState,
      // TODO SPAR-13063 read state from config
      timer: undefined,
      videoClips: videoState,
      watermark,
      transcription: {},
      ...getKeyTextAssets(keyTextAssets, omit(keySlideshowAssets, 'mainImage')),
    };

    return createState(state);
  },
  removeDisabledSoundwave,
  applyConfigTransforms,
  fixTemplateConfig,
);

export function createModifiedState(
  baseState: VideoTemplateState,
  transformer: (
    baseContent: VideoTemplateStateContent,
  ) => VideoTemplateStateContent,
): VideoTemplateState {
  const baseContent = getStateContent(baseState);
  return createState(transformer(baseContent));
}

export function overrideStateContent(
  state: VideoTemplateState,
  overrides: Partial<VideoTemplateStateContent>,
): VideoTemplateState {
  return createModifiedState(state, content => ({
    ...content,
    ...overrides,
  }));
}

export function createStateFromTemplate(
  template: PodcastWorkflowTemplate,
): VideoTemplateState {
  const aspectRatio = getAspectRatio(template);

  return overrideStateContent(
    createStateFromConfig(template.previewConfiguration),
    { aspectRatio, template },
  );
}

export function createTemplateEditorState({
  aspectRatio,
  backgroundColor = DEFAULT_BG_COLOR,
  canvas,
  container,
  hasCubeEffect = false,
  introOutro = {},
  isComplex = false,
  layers = DEFAULT_LAYER_STATE,
  mainImage = DEFAULT_MAIN_IMAGE,
  progress = DEFAULT_PROGRESS_STATE,
  slideEffectText = {},
  slideshow = DEFAULT_SLIDESHOW_STATE,
  soundwave,
  textOverlays = DEFAULT_ORDERED_STATE,
  timer,
  videoClips = DEFAULT_ORDERED_STATE,
  watermark,
  transcription = {},
}: Partial<VideoTemplateEditorConfig>): VideoTemplateState {
  return createState({
    aspectRatio,
    backgroundColor,
    canvas,
    container,
    hasCubeEffect,
    introOutro,
    isComplex,
    layers,
    mainImage,
    originalLayers: layers.order,
    progress,
    slideEffectText,
    slideshow,
    soundwave,
    textOverlays,
    timer,
    videoClips,
    watermark,
    patch: DEFAULT_PATCH,
    transcription,
    ...DEFAULT_KEY_TEXT_ASSETS,
  });
}
